import type { FolgeAuswahlZeile } from '../../services/folgeunterlagen/folgeunterlagen';
import type { ErzeugungsErgebnis, FolgeArt } from '../../services/folgeunterlagen/typen';
import { SPRUNGZIEL_JE_UNTERLAGE, type UebergabeSprungziel } from './uebergabeLogic';
import type { FreigabePruefung, Planungsfrage, Planungsphase } from './types';

/**
 * Anzeige der Folgeunterlagen (Karte „Folgeunterlagen", Rueckfrage im
 * Freigabe-Gate) — pure Regeln und Saetze, ohne React.
 *
 * Die Haken der Auswahl sind Anzeigezustand der Komponente: sie haelt nur,
 * was der Anwender ANGEKLICKT hat (`FolgeHaken`); was gilt, rechnet
 * `istGewaehlt` aus der Vorauswahl und diesen Klicks. So greift die
 * Vorauswahl auch dann, wenn der Bestand erst nach dem Oeffnen gelesen ist —
 * ohne Effekt, der Zustand nachzieht.
 */

/** Angeklickte Haken je Art; eine fehlende Art folgt der Vorauswahl. */
export type FolgeHaken = Partial<Record<FolgeArt, boolean>>;

/**
 * Ist die Planung so weit, dass die Folgeunterlagen angelegt werden duerfen?
 * Abgeschlossen — oder ein Katalog steht und das Freigabe-Gate ist offen.
 * Ohne Fragen meldet `pruefeFreigabe` zwar `freigabeMoeglich`, erfuellt ist
 * dann aber nichts.
 */
export const folgeunterlagenMoeglich = (lage: {
    phase: Pick<Planungsphase, 'id'> | null;
    fragen: readonly Pick<Planungsfrage, 'id'>[];
    freigabe: Pick<FreigabePruefung, 'freigabeMoeglich'>;
    abgeschlossen: boolean;
}): boolean => lage.abgeschlossen || (!!lage.phase && lage.fragen.length > 0 && lage.freigabe.freigabeMoeglich);

export const FOLGE_NOCH_NICHT_SATZ = 'Die Folgeunterlagen lassen sich anlegen, sobald die Planungsphase erfüllt ist '
    + '(alle haftungsrelevanten Fragen beantwortet oder durch eine versendete Bedenkenanzeige gedeckt).';

export const FOLGE_FUSS_SATZ = 'Die Entwürfe werden nicht freigegeben und nicht versendet. Vorhandene Unterlagen bleiben unverändert.';

export const FOLGE_UNGESPEICHERT_SATZ = 'Ungespeicherte Angaben der Grundlagen fließen nicht ein. Die Entwürfe entstehen aus dem gespeicherten Stand.';

/** Gilt der Haken dieser Zeile? Nie, wenn sie nicht waehlbar ist. */
export const istGewaehlt = (zeile: Pick<FolgeAuswahlZeile, 'art' | 'waehlbar' | 'vorgewaehlt'>, haken: FolgeHaken): boolean => {
    if (!zeile.waehlbar) return false;
    const geklickt = haken[zeile.art];
    return typeof geklickt === 'boolean' ? geklickt : zeile.vorgewaehlt;
};

/** Die geltenden Haken als Arten, in der Reihenfolge der Zeilen. */
export const gewaehlteArten = (
    zeilen: readonly Pick<FolgeAuswahlZeile, 'art' | 'waehlbar' | 'vorgewaehlt'>[],
    haken: FolgeHaken,
): FolgeArt[] => zeilen.filter(z => istGewaehlt(z, haken)).map(z => z.art);

/** Sprung „Zum Modul" je Folgeunterlage — jede `FolgeArt` ist eine
 *  `Folgeunterlage` des Steckbriefs (dieselbe Zuordnung wie
 *  `SPRUNGZIEL_JE_AUSGABE` von der Ausgabenkennung aus). */
export const sprungzielFuer = (art: FolgeArt): UebergabeSprungziel => SPRUNGZIEL_JE_UNTERLAGE[art];

/** Die Rueckfrage vor dem Abschluss — zwei Saetze statt `window.confirm`. */
export const abschlussRueckfrage = (restrisiken: number): { frage: string; rest: string | null } => ({
    frage: 'Planungsphase abschließen?',
    rest: restrisiken > 0 ? `${restrisiken} Punkt(e) gehen als Restrisikoliste in die Ausführungsphase über.` : null,
});

export interface FolgeErgebnisAnzeige {
    art: FolgeArt;
    titel: string;
    /** Kleine Marke: „angelegt", „besteht bereits", „Fehler". */
    marke: string;
    fehler: boolean;
    /** Bestand bzw. Fehler als Satz; `null` bei `angelegt`. */
    satz: string | null;
    uebernommen: string[];
    /** „3 Angaben übernommen" — `null` ohne Uebernahme. */
    uebernommenText: string | null;
    offen: string[];
    sprungziel: UebergabeSprungziel;
}

export const ergebnisAnzeige = (e: ErzeugungsErgebnis, titel: string): FolgeErgebnisAnzeige => {
    const basis = { art: e.art, titel, sprungziel: sprungzielFuer(e.art) };
    if (e.status === 'angelegt') {
        const n = e.uebernommen.length;
        return {
            ...basis, marke: 'angelegt', fehler: false, satz: null,
            uebernommen: [...e.uebernommen],
            uebernommenText: n ? (n === 1 ? '1 Angabe übernommen' : `${n} Angaben übernommen`) : null,
            offen: [...e.offen],
        };
    }
    if (e.status === 'vorhanden') {
        return {
            ...basis, marke: 'besteht bereits', fehler: false,
            satz: e.beschreibung ? `Besteht: ${e.beschreibung}. Unverändert gelassen.` : 'Unverändert gelassen.',
            uebernommen: [], uebernommenText: null, offen: [],
        };
    }
    return {
        ...basis, marke: 'Fehler', fehler: true, satz: e.fehler || 'Unbekannter Fehler.',
        uebernommen: [], uebernommenText: null, offen: [],
    };
};

/**
 * Eine Meldung ueber den ganzen Lauf: „2 Entwürfe angelegt, 1 bestand
 * bereits, 1 Fehler — Einzelheiten unter Folgeunterlagen." Teile mit 0
 * entfallen; ohne Ergebnis `null`.
 */
export const ergebnisMeldung = (ergebnisse: readonly ErzeugungsErgebnis[] | null | undefined): string | null => {
    const liste = ergebnisse || [];
    if (!liste.length) return null;
    const angelegt = liste.filter(e => e.status === 'angelegt').length;
    const vorhanden = liste.filter(e => e.status === 'vorhanden').length;
    const fehler = liste.filter(e => e.status === 'fehler').length;
    const teile: string[] = [];
    if (angelegt) teile.push(angelegt === 1 ? '1 Entwurf angelegt' : `${angelegt} Entwürfe angelegt`);
    if (vorhanden) teile.push(vorhanden === 1 ? '1 bestand bereits' : `${vorhanden} bestanden bereits`);
    if (fehler) teile.push(`${fehler} Fehler`);
    return `${teile.join(', ')}. Einzelheiten unter Folgeunterlagen.`;
};
